import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { ScrollableContainer } from "@/components/ui/scrollbar";
import { WorkoutBlockForm } from "@/components/athlete-profile/workouts/form";
import { useAthleteNameBreadcrumb } from "@/hooks/use-breadcrumb/use-breadcrumb-overrides";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { isValidEntityId } from "@/utils/route-guards";

export const Route = createFileRoute(
  "/(protected)/athletes/$athleteId/workout-blocks/new/",
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    const raw = search.duplicateFrom;
    const numeric = raw ? Number(raw) : NaN;
    return {
      duplicateFrom: isValidEntityId(numeric) ? numeric : undefined,
    };
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { athleteId } = Route.useParams();
  const { duplicateFrom } = Route.useSearch();

  useAthleteNameBreadcrumb(Number(athleteId));

  const navigateBack = () =>
    navigate({
      to: PROTECTED_ROUTES.ATHLETE_DETAIL,
      params: { athleteId },
      search: { tab: "workouts" },
      replace: true,
    });

  const handleCreated = (id: number) => {
    navigate({
      to: PROTECTED_ROUTES.ATHLETE_WORKOUT_BLOCK_DETAIL,
      params: { athleteId, workoutBlockId: String(id) },
      replace: true,
    });
  };

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>
        {t("pages:profile.workouts.form.createTitle")}
      </MobilePageTitle>
      <PageContainer
        className="flex min-h-0 flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        <ScrollableContainer>
          <div className="px-6 pt-6 pb-10 xl:px-12">
            <WorkoutBlockForm
              athleteId={Number(athleteId)}
              duplicateFrom={duplicateFrom}
              onCancel={navigateBack}
              onCreated={handleCreated}
            />
          </div>
        </ScrollableContainer>
      </PageContainer>
    </div>
  );
}
